import React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import axios from 'axios';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CardMembershipIcon from '@mui/icons-material/CardMembership';

const StatCard = ({ title, value, subtitle, icon }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4">{value}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ActivitySection = ({ title, items, renderItem }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <List>
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && <Divider />}
          <ListItem>
            {renderItem(item)}
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  </Paper>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/activity`)
        ]);
        
        setStats(statsRes.data.stats);
        setActivity({
          recentUsers: activityRes.data.activity?.recentUsers || [],
          recentPosts: activityRes.data.activity?.recentPosts || [],
          recentSubscriptions: activityRes.data.activity?.recentSubscriptions || []
        });
      } catch (err) {
        console.error('Dashboard error:', err);
        setError(err.response?.data?.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* User Stats */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Users"
            value={stats.users.total}
            subtitle={`${stats.users.newToday} new today`}
            icon={<PeopleIcon color="primary" sx={{ fontSize: 40 }} />}
          />
        </Grid>

        {/* Post Stats */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Posts"
            value={stats.posts.total}
            subtitle={`${stats.posts.today} posts today`}
            icon={<ArticleIcon color="primary" sx={{ fontSize: 40 }} />}
          />
        </Grid>

        {/* Subscription Stats */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Active Subscribers"
            value={stats.subscriptions.activeSubscribers}
            icon={<CardMembershipIcon color="primary" sx={{ fontSize: 40 }} />}
          />
        </Grid>

        {/* Revenue Stats */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Revenue"
            value={`$${Number(stats.revenue.total || 0).toFixed(2)}`}
            subtitle={`${stats.revenue.transactions} transactions`}
            icon={<MonetizationOnIcon color="primary" sx={{ fontSize: 40 }} />}
          />
        </Grid>

        {/* Recent Activity Sections */}
        <Grid item xs={12} md={4}>
          <ActivitySection
            title="Recent Users"
            items={activity.recentUsers}
            renderItem={(user) => (
              <ListItemText
                primary={user.username}
                secondary={new Date(user.created_at).toLocaleDateString()}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ActivitySection
            title="Recent Posts"
            items={activity.recentPosts}
            renderItem={(post) => (
              <ListItemText
                primary={post.username}
                secondary={post.content.substring(0, 50) + '...'}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ActivitySection
            title="Recent Subscriptions"
            items={activity.recentSubscriptions}
            renderItem={(sub) => (
              <ListItemText
                primary={sub.username}
                secondary={`Plan: ${sub.plan_type}`}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard; 